import type { PlanViewData } from "@earthbrain/foundation-excavation-sdk/data-io";

export interface PlanViewMeshFlat {
	readonly vertices: number[];
	readonly indices: number[];
}

interface Rect2D {
	readonly x: number;
	readonly z: number;
	readonly width: number;
	readonly depth: number;
}

interface Point2D {
	readonly x: number;
	readonly z: number;
}

function rectToPolygon(rect: Rect2D): ReadonlyArray<Point2D> {
	return [
		{ x: rect.x, z: rect.z },
		{ x: rect.x + rect.width, z: rect.z },
		{ x: rect.x + rect.width, z: rect.z + rect.depth },
		{ x: rect.x, z: rect.z + rect.depth },
	];
}

/**
 * Convert SDK PlanViewData to a flat triangle mesh via fan triangulation.
 *
 * Assumes all polygons are convex (rect, HOMEBASE, and their outward offsets).
 * If SDK adds concave polygons in the future, switch to ear-clipping.
 *
 * Output vertices are y=0 padded for the existing ECEF transform pipeline.
 */
export function buildPlanViewMesh(planView: PlanViewData): PlanViewMeshFlat {
	const vertices: number[] = [];
	const indices: number[] = [];

	const pushConvexPolygon = (poly: ReadonlyArray<Point2D>): void => {
		if (poly.length < 3) return;
		const startIdx = vertices.length / 3;
		for (const p of poly) {
			vertices.push(p.x, 0, p.z);
		}
		for (let i = 1; i < poly.length - 1; i++) {
			indices.push(startIdx, startIdx + i, startIdx + i + 1);
		}
	};

	for (const f of planView.foundations) {
		pushConvexPolygon(f.foundationPolygon ?? rectToPolygon(f.foundationRect));
		pushConvexPolygon(f.extraPolygon ?? rectToPolygon(f.extraRect));
	}
	for (const b of planView.beams) {
		pushConvexPolygon(b.foundationPolygon ?? rectToPolygon(b.foundationRect));
		pushConvexPolygon(b.extraPolygon ?? rectToPolygon(b.extraRect));
	}
	for (const s of planView.slabs) {
		pushConvexPolygon(s.extraPolygon ?? rectToPolygon(s.extraRect));
	}

	return { vertices, indices };
}
